import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { useMutation, useClient } from 'Data/Api.js'
import {
  notifyError,
  notifySuccess,
  useNotifications,
} from 'Logic/Notifications.js'
import { useDataSubmit, useDataChange, useDataValue } from 'Simple/Data.js'
import { textToNumber, numberMoney } from 'Simple/Data/format.js'
import { PAYMENTS_PAYMENT_TYPES } from 'Data/common-constants.js'

import mutationCash from './mutation_cash.graphql.js'
import mutationCheck from './mutation_check.graphql.js'
import mutationCreditCard from './mutation_credit_card.graphql.js'
import mutationCreditCardManual from './mutation_credit_card_manual.graphql.js'
import mutationOther from './mutation_other.graphql.js'
import mutationBankAccount from './mutation_bank_account.graphql.js'
import mutationPersonPaymentAccount from './mutation_person_payment_account.graphql.js'
import queryPatientDueNow from './query_patient_due_now.graphql.js'
import queryPaymentReceiptDownload from './query_payment_receipt_download.graphql.js'
import mutationPaymentReceiptSendEmail from './mutation_payment_receipt_send_email.graphql.js'
import { validate } from './helpers.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let onActionFormSubmit = useDataOnActionFormSubmit(props)
  let onDueAmountSubmit = useDataOnDueAmountSubmit(props)

  return async function onSubmit({ value, originalValue, args, change }) {
    switch (args.type) {
      // temp form blocking while querying due amount
      case 'due_amount_submit': {
        return onDueAmountSubmit({ value, args, change })
      }
      case 'form_submit': {
        return onActionFormSubmit({ value, change })
      }
      default: {
      }
    }
  }
}

function useDataOnDueAmountSubmit(props) {
  let client = useClient()

  let patient_uuid = useDataValue({
    context: 'message',
    path: 'params.patient_uuid',
    viewPath: props.viewPath,
  })

  return async function onAction({ value, args, change }) {
    // dont update amount, if we are working on receivable
    if (value.receivable_id) return

    let mutationResponse = await client
      .query(queryPatientDueNow, {
        patient_id: patient_uuid,
        payment_account_id: args.payment_account_id,
      })
      .toPromise()

    if (mutationResponse.error) {
      change(next => {
        next.amount = numberMoney(0)
      })
      return
    }

    change(next => {
      next.amount = numberMoney(
        mutationResponse.data.financial_patient_due_amount?.[0]?.due_now ?? 0
      )
    })
  }
}

function useDataOnActionFormSubmit(props) {
  let [, notify] = useNotifications()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let paymentSubmit = useDataSubmit({
    context: 'payment',
    viewPath: props.viewPath,
  })
  let refreshEmbeddedSection = useDataChange({
    context: 'global',
    path: 'refresh_embedded_section',
    viewPath: props.viewPath,
  })
  let parent_organization_id = useDataValue({
    context: 'global',
    path: 'current_location.parent_company.id',
    viewPath: props.viewPath,
  })
  let location_id = useDataValue({
    context: 'global',
    path: 'current_location._id',
    viewPath: props.viewPath,
  })
  let person_id = useDataValue({
    context: 'post_payment_info',
    path: 'patient.person.id',
    viewPath: props.viewPath,
  })
  let patient_uuid = useDataValue({
    context: 'message',
    path: 'params.patient_uuid',
    viewPath: props.viewPath,
  })
  let [, executeMutationCashPayment] = useMutation(mutationCash)
  let [, executeMutationOtherPayment] = useMutation(mutationOther)
  let [, executeMutationCreditCardPayment] = useMutation(mutationCreditCard)
  let [, executeMutationCreditCardManualPayment] = useMutation(
    mutationCreditCardManual
  )
  let [, executeMutationBankAccountPayment] = useMutation(mutationBankAccount)
  let [, executeMutationCheckPayment] = useMutation(mutationCheck)
  let [, executeMutationPersonPaymentAccount] = useMutation(
    mutationPersonPaymentAccount
  )
  let [, executeMutationPaymentReceiptSendEmail] = useMutation(
    mutationPaymentReceiptSendEmail
  )
  let client = useClient()

  return async function onAction({ value, change }) {
    let validation = validate(value)

    if (validation.isInvalid) {
      return failure(new Error(validation.errors[0]))
    }

    let baseVariables = {
      amount: textToNumber(value.amount),
      reference_number: value.reference_number,
      notes: value.notes,
      location_id,
      payment_type_id: value.payment_method.data.payment_type_id,
      patient_id: patient_uuid,
      payment_account_id: value.payment_account.data.payment_account_id,
      auto_apply_to_unpaid_receivables: value.auto_apply_to_unpaid_receivables,
      insured_id: value.payment_account.data.insured_id,
      receivable_id: value.receivable_id,
    }

    // we have to create new payment account
    if (!baseVariables.payment_account_id) {
      try {
        let mutationResponse = await executeMutationPersonPaymentAccount({
          payer_person_id: value.payment_account.data.payer_person.id,
          parent_organization_id,
        })

        if (mutationResponse.error) throw mutationResponse.error

        let { payment_account: new_payment_account } =
          mutationResponse.data.insert_vaxiom_person_payment_accounts_one

        // we need to update the value, because the new ids wont match previous temporary uuid
        change(next => {
          next.payment_account.selected_id = `${new_payment_account.id}`
          next.payment_account.data.payment_account_id = new_payment_account.id
        })

        baseVariables.payment_account_id = new_payment_account.id
      } catch (error) {
        return failure(error)
      }
    }

    switch (value.payment_method.data.payments_payment_type) {
      case PAYMENTS_PAYMENT_TYPES.CashPayment: {
        try {
          let mutationResponse = await executeMutationCashPayment(baseVariables)

          if (mutationResponse.error) throw mutationResponse.error

          return success(
            mutationResponse.data.payments_post_payment_cash.payment_id
          )
        } catch (error) {
          return failure(error)
        }
      }
      case PAYMENTS_PAYMENT_TYPES.CheckPayment: {
        try {
          let fallback_payer_person = value.payment_account.data.payer_person
          let mutationResponse = await executeMutationCheckPayment({
            ...baseVariables,
            // prettier-ignore
            payer_name: value.check.payer_name || `${fallback_payer_person.first_name} ${fallback_payer_person.first_name}`,
            check_number: value.check.number,
          })

          if (mutationResponse.error) throw mutationResponse.error

          return success(
            mutationResponse.data.payments_post_payment_check.payment_id
          )
        } catch (error) {
          return failure(error)
        }
      }
      case PAYMENTS_PAYMENT_TYPES.OtherPayment: {
        try {
          let mutationResponse = await executeMutationOtherPayment(
            baseVariables
          )

          if (mutationResponse.error) throw mutationResponse.error

          return success(
            mutationResponse.data.payments_post_payment_other.payment_id
          )
        } catch (error) {
          return failure(error)
        }
      }
      case PAYMENTS_PAYMENT_TYPES.BankAccountPayment: {
        try {
          let token_id = await paymentSubmit({ type: 'tokenize' })

          // true means invalid in this context
          if (token_id === true) return true
          if (token_id === null) {
            throw new Error(
              'There was an issue taking the payment. Please try again.'
            )
          }

          let mutationResponse = await executeMutationBankAccountPayment({
            ...baseVariables,
            token_id,
          })

          if (mutationResponse.error)
            throw new Error(
              'There was an issue taking the payment. Please try again.'
            )

          return success(
            mutationResponse.data.payments_post_payment_bank_account.payment_id
          )
        } catch (error) {
          return failure(error)
        }
      }
      case PAYMENTS_PAYMENT_TYPES.CreditCardPayment: {
        try {
          let token_id = await paymentSubmit({ type: 'tokenize' })

          // true means invalid in this context
          if (token_id === true) return true
          if (token_id === null) {
            throw new Error(
              'There was an issue taking the payment. Please try again.'
            )
          }

          let mutationResponse = await executeMutationCreditCardPayment({
            ...baseVariables,
            token_id,
          })

          if (mutationResponse.error)
            throw new Error(
              'There was an issue taking the payment. Please try again.'
            )

          return success(
            mutationResponse.data.payments_post_payment_credit_card.payment_id
          )
        } catch (error) {
          return failure(error)
        }
      }
      case PAYMENTS_PAYMENT_TYPES.CreditCardManualPayment: {
        try {
          let mutationResponse = await executeMutationCreditCardManualPayment({
            ...baseVariables,
            transaction_id: value.credit_card_manual.transaction_id,
            effective_date: value.credit_card_manual.effective_date,
          })

          if (mutationResponse.error) throw mutationResponse.error

          return success(
            mutationResponse.data.payments_post_payment_credit_card_manual
              .payment_id
          )
        } catch (error) {
          return failure(error)
        }
      }
      default: {
        return failure(new Error('Something went wrong'))
      }
    }

    /**
     * @param {number} payment_id
     */
    async function printReceipt(payment_id) {
      notify(notifySuccess('Preparing PDF'))

      let mutationResponse = await client
        .query(queryPaymentReceiptDownload, {
          payment_id,
          person_id,
        })
        .toPromise()

      if (mutationResponse.error) return failedPdf()

      let url = mutationResponse.data.vaxiom_payment_receipt_download.url

      if (!url) return failedPdf()

      let pdfWindow = window.open(url, '_blank')
      pdfWindow.onload = () => pdfWindow.print()

      function failedPdf() {
        notify(notifyError('Failed to prepare PDF'))
      }
    }

    /**
     * @param {number} payment_id
     * @returns {boolean}
     */
    function success(payment_id) {
      notify(notifySuccess('Payment was successful'))

      setFlowTo(normalizePath(props.viewPath, '../../No'))
      // TODO: imho, this is bad ux, user will loose filters, toggles in core ui etc...
      refreshEmbeddedSection(Date.now())

      if (value.receipt.checkbox.print) {
        printReceipt(payment_id)
      }

      if (value.receipt.checkbox.email) {
        executeMutationPaymentReceiptSendEmail({
          payment_id,
          person_id,
          email: value.receipt.email,
        })
      }

      return false // false means success
    }

    /**
     * @param {Error} error
     * @returns {boolean}
     */
    function failure(error) {
      notify(notifyError(error?.message ?? 'Something went wrong'))

      return true // true means fail
    }
  }
}
